import { ASSET_TYPE_PROPERTY } from '../../constants/PropertyConst';
import { ENVIRONMENT, PROPERTY_SERVICE_URL } from '../../env/Configurations';
import {
  abortMultipartUpload,
  calculateFileChecksum,
  calculateTotalChunks,
  CHUNK_SIZE,
  completeMultipartUpload,
  convertFileToBase64,
} from '../../utils/commonUtils';
import { notificationManager } from '../../utils/NotificationUtils';
import { getPreSignedUrl } from '../../utils/PropertyAPIUtils';

const uploadRatioImage = async (imgObj, ratio, groupId, propertyType, hotelId) => {
  const imageFile = imgObj?.fileData[ratio]?.file;
  const imageFileSize = imageFile?.size;

  if (imageFile && imageFileSize < 2 * 1024 * 1024) {
    const base64 = await convertFileToBase64(imageFile);
    const input = {
      contentLength: imageFile?.size,
      contentType: imageFile?.type,
      filename: imageFile?.name,
      skipContentValidation: false,
      type: ASSET_TYPE_PROPERTY,
      contents: base64,
      groupId,
      propertyType,
    };

    const res = await getPreSignedUrl(input);

    if (res?.data?.getPreSignUrlCMS?.threatFound === true) {
      return { key: null, threatFound: true };
    }

    if (res?.data?.getPreSignUrlCMS?.key) {
      return { key: res.data.getPreSignUrlCMS.key, threatFound: false };
    } else {
      throw new Error(
        `Error while uploading cropped image for ratio ${ratio}: ${imgObj?.fileName}`,
      );
    }
  } else {
    const totalChunks = calculateTotalChunks(imageFileSize);
    let uploadId = null;

    const fileChecksum = await calculateFileChecksum(imageFile);
    const fileSize = imageFile.size;

    const startUploadResponse = await fetch(
      `${PROPERTY_SERVICE_URL}/startMultipartUpload`,
      {
        method: 'POST',
        body: JSON.stringify({
          groupId,
          type: ASSET_TYPE_PROPERTY,
          contentType: imageFile?.type,
          filename: imgObj?.fileName,
          hotelId,
          propertyType,
          IsCMSRequest: true,
          fileSize,
          fileChecksum,
        }),
      },
    );

    const chunkData = await startUploadResponse.json();
    uploadId = chunkData?.data?.key;

    try {
      for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
        const start = chunkIndex * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, imageFile.size);
        const fileChunk = imageFile.slice(start, end);

        const base64Chunk = await convertFileToBase64(fileChunk);
        const response = await fetch(
          `${PROPERTY_SERVICE_URL}/uploadMultipartChunk`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              groupId,
              hotelId,
              uploadId,
              sequenceNumber: chunkIndex + 1,
              contents: base64Chunk,
            }),
          },
        );

        const responseData = await response.json();

        if (!response.ok) {
          throw new Error(`Upload failed: ${responseData.message}`);
        }
      }

      const completeResponse = await completeMultipartUpload(
        groupId,
        hotelId,
        uploadId,
      );

      if (
        completeResponse?.status === 'ok' &&
        completeResponse?.data?.status === true
      ) {
        return { key: completeResponse?.data?.message, threatFound: false };
      } else {
        throw new Error('Failed to complete upload');
      }
    } catch (error) {
      if (uploadId) {
        await abortMultipartUpload(uploadId, groupId, hotelId);
      }
      throw error;
    }
  }
};

export const getProcessedImages = async (imageList, groupId, propertyType, hotelId) => {
  let processedImageList = [];

  const imgListProcessingPromiseArr = imageList?.map(async (imgObj) => {
    return await new Promise(async (resolve, reject) => {
      const processedImageRatioSet = {
        index: imgObj?.index,
        fileName: imgObj?.fileName,
      };

      const ratioKeys =
        imgObj?.fileData && typeof imgObj?.fileData === 'object'
          ? Object.keys(imgObj.fileData)
          : [];

      if (!imgObj.isTouched) {
        ratioKeys.forEach((ratio) => {
          processedImageRatioSet[ratio] = imgObj[ratio];
        });
        resolve(processedImageRatioSet);
        return;
      }

      const isGif = imgObj?.fileData?.master?.file?.type === 'image/gif';

      try {
        if (ratioKeys.includes('master')) {
          const masterResult = await uploadRatioImage(imgObj, 'master', groupId, propertyType, hotelId);

          if (masterResult.threatFound) {
            notificationManager('danger', 'Threat detected in uploaded image. Upload blocked.');
            reject('Threat detected in uploaded image.');
            return;
          }

          processedImageRatioSet['master'] = masterResult.key;
        }

        const remainingRatios = ratioKeys.filter((r) => r !== 'master');
        if (remainingRatios.length > 0) {
          const results = await Promise.all(
            remainingRatios.map(async (ratio) => {
              const result = await uploadRatioImage(imgObj, ratio, groupId, propertyType, hotelId);
              return { ratio, result };
            }),
          );

          results.forEach(({ ratio, result }) => {
            processedImageRatioSet[ratio] = result.key;
          });
        }

        resolve(processedImageRatioSet);
      } catch (error) {
        if (ENVIRONMENT === 'dev') {
          console.log(error?.message || error, 'error');
        }
        if (isGif) {
          notificationManager('danger', 'Error processing GIF image.');
        } else {
          notificationManager('danger', 'Error processing image.');
        }
        reject(error);
      }
    });
  });

  try {
    const settledResults = await Promise.allSettled(imgListProcessingPromiseArr);
    processedImageList = settledResults
      .filter((result) => result.status === 'fulfilled')
      .map((result) => result.value);
    return processedImageList;
  } catch (error) {
    if (ENVIRONMENT === 'dev') {
      console.log(error?.message || error, 'error');
    }
    notificationManager('danger', 'Error processing image');
    return error;
  }
};
